import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, ArrowLeft, ArrowRight, Plus, Minus, Trash2, Package, 
  Wrench, DollarSign, ChevronDown, ChevronRight, Calculator, Check, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { ScrollArea } from "../components/ui/scroll-area";

const UNIT_LABELS = {
  sq_ft: "sq ft",
  linear_ft: "linear ft",
  per_room: "room(s)",
  per_door: "door(s)",
  per_window: "window(s)",
  per_fixture: "fixture(s)",
  per_cabinet: "cabinet(s)",
  per_hour: "hour(s)",
  per_frame: "frame(s)",
  per_closet: "closet(s)",
  per_hallway: "hallway(s)"
};

// Room-based pricing for Painting category (reusable for other categories)
const ROOM_PRICING = {
  painting: {
    all_bedrooms: { low: 800, high: 2000, label: "All bedrooms" },
    master_bedroom: { low: 350, high: 700, label: "Master bedroom" },
    bedroom_2: { low: 250, high: 500, label: "Bedroom 2" },
    bedroom_3: { low: 250, high: 500, label: "Bedroom 3" },
    living_room: { low: 400, high: 900, label: "Living room" },
    dining_room: { low: 300, high: 600, label: "Dining room" },
    kitchen: { low: 350, high: 800, label: "Kitchen" },
    bathrooms: { low: 200, high: 500, label: "Bathroom(s)" },
    hallways: { low: 250, high: 600, label: "Hallways" },
    other: { low: 200, high: 600, label: "Other" }
  },
  cleaning: {
    all_bedrooms: { low: 100, high: 200, label: "All bedrooms" },
    master_bedroom: { low: 50, high: 100, label: "Master bedroom" },
    bedroom_2: { low: 40, high: 80, label: "Bedroom 2" },
    bedroom_3: { low: 40, high: 80, label: "Bedroom 3" },
    living_room: { low: 60, high: 120, label: "Living room" },
    dining_room: { low: 40, high: 80, label: "Dining room" },
    kitchen: { low: 80, high: 150, label: "Kitchen" },
    bathrooms: { low: 60, high: 120, label: "Bathroom(s)" },
    hallways: { low: 30, high: 60, label: "Hallways" },
    other: { low: 40, high: 100, label: "Other" }
  }
};

// Categories that use room-based selection
const ROOM_BASED_CATEGORIES = ["painting"];

export default function EstimateBuilder() {
  const { categoryId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categoryId || "");
  const [currentCatalog, setCurrentCatalog] = useState({ lineItems: [], bundles: [], addons: [] });
  const [loading, setLoading] = useState(true);
  
  // ==================== SHARED PROJECT CART STATE ====================
  // Multi-category project estimate - persists across category switches
  const [projectCart, setProjectCart] = useState({
    // Traditional items: { [categorySlug]: [{ item_id, item_name, quantity, labor_low, labor_high, materials_low, materials_high, unit_type }] }
    items: {},
    // Bundles: { [categorySlug]: [{ bundle_id, bundle_name, labor_low, labor_high, materials_low, materials_high }] }
    bundles: {},
    // Room selections: { [categorySlug]: { rooms: [], otherDescription: "" } }
    rooms: {},
    // Add-ons: { [categorySlug]: [{ addon_id, addon_name, modifier_type, modifier_value, applies_to }] }
    addons: {}
  });

  // Cache for catalog data across categories (to avoid refetching)
  const [catalogCache, setCatalogCache] = useState({});

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      
      // Find painting category by default or use passed categoryId
      if (categoryId) {
        setSelectedCategory(categoryId);
      } else {
        const paintingCat = response.data.find(c => c.slug === "painting");
        if (paintingCat) {
          setSelectedCategory(paintingCat.category_id);
        } else if (response.data.length > 0) {
          setSelectedCategory(response.data[0].category_id);
        }
      }
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  }, [categoryId]);

  const fetchCatalogData = useCallback(async () => {
    const category = categories.find(c => c.category_id === selectedCategory);
    if (!category) return;
    
    const categoryName = category.name;
    
    // Check cache first
    if (catalogCache[categoryName]) {
      setCurrentCatalog(catalogCache[categoryName]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [itemsRes, bundlesRes, addonsRes] = await Promise.all([
        axios.get(`${API}/catalog/line-items?category=${categoryName}`),
        axios.get(`${API}/catalog/bundles?category=${categoryName}`),
        axios.get(`${API}/catalog/addons?category=${categoryName}`)
      ]);
      
      const catalogData = {
        lineItems: itemsRes.data,
        bundles: bundlesRes.data,
        addons: addonsRes.data
      };
      
      // Cache it
      setCatalogCache(prev => ({ ...prev, [categoryName]: catalogData }));
      setCurrentCatalog(catalogData);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  }, [categories, selectedCategory, catalogCache]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedCategory && categories.length > 0) {
      fetchCatalogData();
    }
  }, [selectedCategory, categories, fetchCatalogData]);

  // Get current category data
  const selectedCategoryData = categories.find(c => c.category_id === selectedCategory);
  const categorySlug = selectedCategoryData?.slug || "painting";
  const categoryName = selectedCategoryData?.name || "Painting";
  const isRoomBasedCategory = ROOM_BASED_CATEGORIES.includes(categorySlug);

  // Get current category's cart data
  const currentItems = projectCart.items[categorySlug] || [];
  const currentBundles = projectCart.bundles[categorySlug] || [];
  const currentRooms = projectCart.rooms[categorySlug] || { rooms: [], otherDescription: "" };
  const currentAddons = projectCart.addons[categorySlug] || [];

  // Group line items by subcategory (for non-room-based categories)
  const groupedItems = useMemo(() => {
    const groups = {};
    currentCatalog.lineItems.forEach(item => {
      const subcat = item.subcategory || "General";
      if (!groups[subcat]) {
        groups[subcat] = [];
      }
      groups[subcat].push(item);
    });
    return groups;
  }, [currentCatalog.lineItems]);

  // ==================== CALCULATE COMBINED TOTALS ACROSS ALL CATEGORIES ====================
  const projectTotals = useMemo(() => {
    let totalLow = 0;
    let totalHigh = 0;
    const categoryBreakdown = {};
    
    // Calculate room-based totals
    Object.entries(projectCart.rooms).forEach(([catSlug, roomData]) => {
      if (roomData.rooms.length === 0) return;
      const pricing = ROOM_PRICING[catSlug];
      if (!pricing) return;
      
      let catLow = 0;
      let catHigh = 0;
      roomData.rooms.forEach(roomKey => {
        const roomPrice = pricing[roomKey];
        if (roomPrice) {
          catLow += roomPrice.low;
          catHigh += roomPrice.high;
        }
      });
      
      // Apply add-ons for this category
      const catAddons = projectCart.addons[catSlug] || [];
      catAddons.forEach(addon => {
        if (addon.modifier_type === "percent") {
          const pct = addon.modifier_value / 100;
          catLow += catLow * pct;
          catHigh += catHigh * pct;
        } else {
          catLow += addon.modifier_value;
          catHigh += addon.modifier_value;
        }
      });
      
      if (catLow > 0 || catHigh > 0) {
        const catName = categories.find(c => c.slug === catSlug)?.name || catSlug;
        categoryBreakdown[catName] = { low: Math.round(catLow), high: Math.round(catHigh), type: "rooms" };
        totalLow += catLow;
        totalHigh += catHigh;
      }
    });
    
    // Calculate item-based totals
    Object.entries(projectCart.items).forEach(([catSlug, items]) => {
      if (items.length === 0) return;
      
      let laborLow = 0;
      let laborHigh = 0;
      let materialsLow = 0;
      let materialsHigh = 0;
      
      items.forEach(item => {
        laborLow += item.labor_low * item.quantity;
        laborHigh += item.labor_high * item.quantity;
        materialsLow += item.materials_low * item.quantity;
        materialsHigh += item.materials_high * item.quantity;
      });
      
      // Add bundles
      const catBundles = projectCart.bundles[catSlug] || [];
      catBundles.forEach(bundle => {
        laborLow += bundle.labor_low || 0;
        laborHigh += bundle.labor_high || 0;
        materialsLow += bundle.materials_low || 0;
        materialsHigh += bundle.materials_high || 0;
      });
      
      // Apply add-ons
      const catAddons = projectCart.addons[catSlug] || [];
      let addonLaborLow = 0, addonLaborHigh = 0, addonMatLow = 0, addonMatHigh = 0;
      
      catAddons.forEach(addon => {
        if (addon.modifier_type === "percent") {
          const pct = addon.modifier_value / 100;
          if (addon.applies_to === "labor" || addon.applies_to === "both") {
            addonLaborLow += laborLow * pct;
            addonLaborHigh += laborHigh * pct;
          }
          if (addon.applies_to === "materials" || addon.applies_to === "both") {
            addonMatLow += materialsLow * pct;
            addonMatHigh += materialsHigh * pct;
          }
        } else {
          if (addon.applies_to === "labor" || addon.applies_to === "both") {
            addonLaborLow += addon.modifier_value;
            addonLaborHigh += addon.modifier_value;
          }
          if (addon.applies_to === "materials" || addon.applies_to === "both") {
            addonMatLow += addon.modifier_value;
            addonMatHigh += addon.modifier_value;
          }
        }
      });
      
      const catLow = laborLow + materialsLow + addonLaborLow + addonMatLow;
      const catHigh = laborHigh + materialsHigh + addonLaborHigh + addonMatHigh;
      
      if (catLow > 0 || catHigh > 0) {
        const catName = categories.find(c => c.slug === catSlug)?.name || catSlug;
        categoryBreakdown[catName] = { low: Math.round(catLow), high: Math.round(catHigh), type: "items" };
        totalLow += catLow;
        totalHigh += catHigh;
      }
    });
    
    return {
      totalLow: Math.round(totalLow),
      totalHigh: Math.round(totalHigh),
      categoryBreakdown
    };
  }, [projectCart, categories]);

  // ==================== CART MODIFICATION FUNCTIONS ====================
  
  // Room selection handlers
  const toggleRoom = (roomKey) => {
    const currentRoomList = currentRooms.rooms;
    let newRooms;
    
    if (roomKey === "all_bedrooms") {
      if (currentRoomList.includes("all_bedrooms")) {
        newRooms = currentRoomList.filter(r => r !== "all_bedrooms");
      } else {
        newRooms = [
          ...currentRoomList.filter(r => !["master_bedroom", "bedroom_2", "bedroom_3"].includes(r)),
          "all_bedrooms"
        ];
      }
    } else if (["master_bedroom", "bedroom_2", "bedroom_3"].includes(roomKey)) {
      if (currentRoomList.includes(roomKey)) {
        newRooms = currentRoomList.filter(r => r !== roomKey);
      } else {
        newRooms = [
          ...currentRoomList.filter(r => r !== "all_bedrooms"),
          roomKey
        ];
      }
    } else {
      if (currentRoomList.includes(roomKey)) {
        newRooms = currentRoomList.filter(r => r !== roomKey);
      } else {
        newRooms = [...currentRoomList, roomKey];
      }
    }
    
    setProjectCart(prev => ({
      ...prev,
      rooms: {
        ...prev.rooms,
        [categorySlug]: {
          rooms: newRooms,
          otherDescription: roomKey === "other" && !newRooms.includes("other") ? "" : currentRooms.otherDescription
        }
      }
    }));
  };

  const setOtherDescription = (description) => {
    setProjectCart(prev => ({
      ...prev,
      rooms: {
        ...prev.rooms,
        [categorySlug]: {
          ...prev.rooms[categorySlug],
          otherDescription: description
        }
      }
    }));
  };

  const isBedroomDisabled = (roomKey) => {
    return ["master_bedroom", "bedroom_2", "bedroom_3"].includes(roomKey) && currentRooms.rooms.includes("all_bedrooms");
  };

  // Item handlers
  const addItem = (item) => {
    const existing = currentItems.find(i => i.item_id === item.item_id);
    const newItems = existing 
      ? currentItems.map(i => i.item_id === item.item_id ? { ...i, quantity: i.quantity + 1 } : i)
      : [...currentItems, {
          item_id: item.item_id,
          item_name: item.name,
          quantity: 1,
          labor_low: item.labor_low,
          labor_high: item.labor_high,
          materials_low: item.materials_low,
          materials_high: item.materials_high,
          unit_type: item.unit_type
        }];
    
    setProjectCart(prev => ({
      ...prev,
      items: { ...prev.items, [categorySlug]: newItems }
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    const newItems = quantity <= 0
      ? currentItems.filter(i => i.item_id !== itemId)
      : currentItems.map(i => i.item_id === itemId ? { ...i, quantity } : i);
    
    setProjectCart(prev => ({
      ...prev,
      items: { ...prev.items, [categorySlug]: newItems }
    }));
  };

  const removeItem = (itemId) => {
    setProjectCart(prev => ({
      ...prev,
      items: { ...prev.items, [categorySlug]: currentItems.filter(i => i.item_id !== itemId) }
    }));
  };

  // Bundle handlers
  const toggleBundle = (bundle) => {
    const isSelected = currentBundles.some(b => b.bundle_id === bundle.bundle_id);
    const newBundles = isSelected
      ? currentBundles.filter(b => b.bundle_id !== bundle.bundle_id)
      : [...currentBundles, {
          bundle_id: bundle.bundle_id,
          bundle_name: bundle.name,
          labor_low: bundle.bundle_labor_low || 0,
          labor_high: bundle.bundle_labor_high || 0,
          materials_low: bundle.bundle_materials_low || 0,
          materials_high: bundle.bundle_materials_high || 0
        }];
    
    setProjectCart(prev => ({
      ...prev,
      bundles: { ...prev.bundles, [categorySlug]: newBundles }
    }));
  };

  // Add-on handlers
  const toggleAddon = (addon) => {
    const isSelected = currentAddons.some(a => a.addon_id === addon.addon_id);
    const newAddons = isSelected
      ? currentAddons.filter(a => a.addon_id !== addon.addon_id)
      : [...currentAddons, {
          addon_id: addon.addon_id,
          addon_name: addon.name,
          modifier_type: addon.modifier_type,
          modifier_value: addon.modifier_value,
          applies_to: addon.applies_to
        }];
    
    setProjectCart(prev => ({
      ...prev,
      addons: { ...prev.addons, [categorySlug]: newAddons }
    }));
  };

  // Remove all items from a category
  const clearCategory = (catSlug) => {
    setProjectCart(prev => ({
      items: { ...prev.items, [catSlug]: [] },
      bundles: { ...prev.bundles, [catSlug]: [] },
      rooms: { ...prev.rooms, [catSlug]: { rooms: [], otherDescription: "" } },
      addons: { ...prev.addons, [catSlug]: [] }
    }));
  };

  const handleContinue = () => {
    // Check if there's anything in the project cart
    const hasRooms = Object.values(projectCart.rooms).some(r => r.rooms.length > 0);
    const hasItems = Object.values(projectCart.items).some(items => items.length > 0);
    const hasBundles = Object.values(projectCart.bundles).some(bundles => bundles.length > 0);
    
    if (!hasRooms && !hasItems && !hasBundles) {
      toast.error("Please add at least one item to your project");
      return;
    }
    
    // Validate "other" room descriptions
    for (const [catSlug, roomData] of Object.entries(projectCart.rooms)) {
      if (roomData.rooms.includes("other") && !roomData.otherDescription.trim()) {
        const catName = categories.find(c => c.slug === catSlug)?.name || catSlug;
        toast.error(`Please describe the 'Other' room/area for ${catName}`);
        return;
      }
    }
    
    // Build structured estimate data for all categories
    const estimateData = {
      project_type: "multi_category",
      categories: [],
      totals: projectTotals,
      created_at: new Date().toISOString()
    };
    
    // Add room-based categories
    Object.entries(projectCart.rooms).forEach(([catSlug, roomData]) => {
      if (roomData.rooms.length > 0) {
        const cat = categories.find(c => c.slug === catSlug);
        estimateData.categories.push({
          category_id: cat?.category_id,
          category_name: cat?.name,
          category_slug: catSlug,
          selection_type: "room_based",
          selected_rooms: roomData.rooms,
          other_description: roomData.otherDescription,
          addons: projectCart.addons[catSlug] || []
        });
      }
    });
    
    // Add item-based categories
    Object.entries(projectCart.items).forEach(([catSlug, items]) => {
      if (items.length > 0 || (projectCart.bundles[catSlug] || []).length > 0) {
        const cat = categories.find(c => c.slug === catSlug);
        estimateData.categories.push({
          category_id: cat?.category_id,
          category_name: cat?.name,
          category_slug: catSlug,
          selection_type: "item_based",
          items: items,
          bundles: projectCart.bundles[catSlug] || [],
          addons: projectCart.addons[catSlug] || []
        });
      }
    });
    
    sessionStorage.setItem("estimateData", JSON.stringify(estimateData));
    
    // Navigate to submit request with the primary category (first one with selections)
    const primaryCategory = estimateData.categories[0]?.category_id || selectedCategory;
    navigate(`/submit-request/${primaryCategory}`);
  };

  const formatPrice = (low, high) => {
    if (low === high) return `$${low.toLocaleString()}`;
    return `$${low.toLocaleString()} – $${high.toLocaleString()}`;
  };

  // Count total items across all categories
  const totalItemCount = useMemo(() => {
    let count = 0;
    Object.values(projectCart.rooms).forEach(r => count += r.rooms.length);
    Object.values(projectCart.items).forEach(items => count += items.length);
    Object.values(projectCart.bundles).forEach(bundles => count += bundles.length);
    return count;
  }, [projectCart]);

  const hasProjectSelections = totalItemCount > 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="estimate-builder-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
          
          <div className="flex items-center gap-2">
            <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] hidden sm:flex">
              <Calculator className="w-4 h-4 mr-1" /> Estimate Builder
            </Badge>
            {totalItemCount > 0 && (
              <Badge className="bg-[#7ED957] text-white">
                {totalItemCount} item{totalItemCount !== 1 ? 's' : ''}
              </Badge>
            )}
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="text-[#1F2933]/70"
            >
              <ArrowLeft className="w-5 h-5 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
        {/* Title */}
        <div className="mb-6 lg:mb-8">
          <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] mb-2">
            Build Your Project <span className="text-[#0FA3B1]">(Multi-Service)</span>
          </h1>
          <p className="text-[#1F2933]/60">
            Add services from multiple categories. Your selections are saved as you browse.
          </p>
        </div>

        {/* Category Selector - Acts as a VIEW FILTER, not reset trigger */}
        <div className="mb-6">
          <Label className="text-sm text-[#1F2933]/70 mb-2 block">Browse Service Category</Label>
          <div className="flex items-center gap-3">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-[300px]" data-testid="category-selector">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => {
                  // Show badge if category has selections
                  const hasSelections = 
                    (projectCart.rooms[cat.slug]?.rooms.length > 0) ||
                    (projectCart.items[cat.slug]?.length > 0) ||
                    (projectCart.bundles[cat.slug]?.length > 0);
                  
                  return (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      <div className="flex items-center gap-2">
                        {cat.name}
                        {hasSelections && (
                          <span className="w-2 h-2 rounded-full bg-[#7ED957]"></span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#1F2933]/50 hidden sm:block">
              Switching categories preserves your selections
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Panel - Selection Options for CURRENT Category */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6">
            {loading ? (
              <div className="bg-white rounded-2xl p-12 flex justify-center">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* Room-Based Selection for Painting */}
                {isRoomBasedCategory ? (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                      <h2 className="font-manrope font-semibold text-lg text-[#1F2933] flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-[#0FA3B1]" />
                        Select Rooms for {categoryName}
                      </h2>
                      <p className="text-sm text-[#1F2933]/60 mt-1">
                        Choose all rooms that need {categoryName.toLowerCase()}
                      </p>
                    </div>
                    
                    <div className="p-4 sm:p-6 space-y-3">
                      {Object.entries(ROOM_PRICING[categorySlug] || ROOM_PRICING.painting).map(([roomKey, roomData]) => {
                        const isSelected = currentRooms.rooms.includes(roomKey);
                        const isDisabled = isBedroomDisabled(roomKey);
                        
                        return (
                          <div key={roomKey}>
                            <div
                              className={`p-4 rounded-xl border transition-all ${
                                isDisabled 
                                  ? 'opacity-50 cursor-not-allowed border-gray-100 bg-gray-50' 
                                  : isSelected 
                                    ? 'border-[#0FA3B1] bg-[#0FA3B1]/5 cursor-pointer' 
                                    : 'border-gray-100 hover:border-gray-200 cursor-pointer'
                              }`}
                              onClick={() => !isDisabled && toggleRoom(roomKey)}
                              data-testid={`room-${roomKey}`}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox 
                                  checked={isSelected} 
                                  disabled={isDisabled}
                                  className="pointer-events-none"
                                />
                                <div className="flex-1">
                                  <span className="font-medium text-[#1F2933]">{roomData.label}</span>
                                  {isDisabled && (
                                    <span className="text-xs text-[#1F2933]/50 ml-2">(included in All bedrooms)</span>
                                  )}
                                </div>
                                {isSelected && (
                                  <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1]">
                                    {formatPrice(roomData.low, roomData.high)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {roomKey === "other" && isSelected && (
                              <div className="mt-2 ml-8">
                                <Textarea
                                  placeholder="Please describe the room or area (required)"
                                  value={currentRooms.otherDescription}
                                  onChange={(e) => setOtherDescription(e.target.value)}
                                  className="text-sm"
                                  rows={2}
                                  data-testid="other-room-input"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* Traditional Line Items */
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                      <h2 className="font-manrope font-semibold text-lg text-[#1F2933] flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-[#0FA3B1]" />
                        {categoryName} Items
                      </h2>
                    </div>
                    
                    <ScrollArea className="max-h-[500px]">
                      <Accordion type="multiple" defaultValue={Object.keys(groupedItems)} className="p-2 sm:p-4">
                        {Object.entries(groupedItems).map(([subcategory, items]) => (
                          <AccordionItem key={subcategory} value={subcategory} className="border-b-0 mb-2">
                            <AccordionTrigger className="px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 hover:no-underline">
                              <span className="font-medium text-[#1F2933]">{subcategory}</span>
                              <Badge variant="secondary" className="ml-2">{items.length}</Badge>
                            </AccordionTrigger>
                            <AccordionContent className="pt-2 pb-0">
                              <div className="space-y-2">
                                {items.map(item => {
                                  const selected = currentItems.find(i => i.item_id === item.item_id);
                                  const itemTotal = selected ? {
                                    low: (item.labor_low + item.materials_low) * selected.quantity,
                                    high: (item.labor_high + item.materials_high) * selected.quantity
                                  } : null;
                                  
                                  return (
                                    <div 
                                      key={item.item_id}
                                      className={`p-3 sm:p-4 rounded-xl border transition-all ${
                                        selected 
                                          ? 'border-[#0FA3B1] bg-[#0FA3B1]/5' 
                                          : 'border-gray-100 hover:border-gray-200'
                                      }`}
                                      data-testid={`item-${item.item_id}`}
                                    >
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-[#1F2933]">{item.name}</span>
                                            {item.pricing_mode === "unit_based" && (
                                              <Badge variant="outline" className="text-xs">
                                                per {UNIT_LABELS[item.unit_type] || item.unit_type}
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="text-sm text-[#1F2933]/60 mt-1">
                                            {formatPrice(item.labor_low + item.materials_low, item.labor_high + item.materials_high)}
                                            {item.pricing_mode === "unit_based" && ` / ${UNIT_LABELS[item.unit_type] || item.unit_type}`}
                                          </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                          {selected ? (
                                            <div className="flex items-center gap-2">
                                              <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => updateItemQuantity(item.item_id, selected.quantity - 1)}
                                              >
                                                <Minus className="w-4 h-4" />
                                              </Button>
                                              <Input
                                                type="number"
                                                value={selected.quantity}
                                                onChange={(e) => updateItemQuantity(item.item_id, parseFloat(e.target.value) || 0)}
                                                className="w-16 sm:w-20 text-center h-8"
                                                min="0"
                                                data-testid={`qty-${item.item_id}`}
                                              />
                                              <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => updateItemQuantity(item.item_id, selected.quantity + 1)}
                                              >
                                                <Plus className="w-4 h-4" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                                onClick={() => removeItem(item.item_id)}
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <Button
                                              onClick={() => addItem(item)}
                                              className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white h-8 px-3"
                                              data-testid={`add-${item.item_id}`}
                                            >
                                              <Plus className="w-4 h-4 mr-1" /> Add
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {selected && itemTotal && (
                                        <div className="mt-2 pt-2 border-t border-[#0FA3B1]/20 text-sm">
                                          <span className="text-[#0FA3B1] font-medium">
                                            Line total: {formatPrice(itemTotal.low, itemTotal.high)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </ScrollArea>
                  </div>
                )}

                {/* Bundles */}
                {!isRoomBasedCategory && currentCatalog.bundles.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                      <h2 className="font-manrope font-semibold text-lg text-[#1F2933] flex items-center gap-2">
                        <Package className="w-5 h-5 text-[#7ED957]" />
                        Packages & Bundles
                      </h2>
                    </div>
                    <div className="p-4 sm:p-6 space-y-3">
                      {currentCatalog.bundles.map(bundle => {
                        const isSelected = currentBundles.some(b => b.bundle_id === bundle.bundle_id);
                        return (
                          <div
                            key={bundle.bundle_id}
                            className={`p-4 rounded-xl border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-[#7ED957] bg-[#7ED957]/5' 
                                : 'border-gray-100 hover:border-gray-200'
                            }`}
                            onClick={() => toggleBundle(bundle)}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox checked={isSelected} className="mt-1" />
                              <div className="flex-1">
                                <div className="font-medium text-[#1F2933]">{bundle.name}</div>
                                {bundle.description && (
                                  <p className="text-sm text-[#1F2933]/60 mt-1">{bundle.description}</p>
                                )}
                                {bundle.pricing_type === "fixed" && (
                                  <div className="text-sm text-[#7ED957] font-medium mt-2">
                                    {formatPrice(
                                      (bundle.bundle_labor_low || 0) + (bundle.bundle_materials_low || 0),
                                      (bundle.bundle_labor_high || 0) + (bundle.bundle_materials_high || 0)
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Add-ons - NO PRICING SHOWN */}
                {currentCatalog.addons.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                      <h2 className="font-manrope font-semibold text-lg text-[#1F2933] flex items-center gap-2">
                        <Plus className="w-5 h-5 text-[#0FA3B1]" />
                        Add-ons & Modifiers
                      </h2>
                      <p className="text-sm text-[#1F2933]/60 mt-1">
                        Select any that apply to your {categoryName.toLowerCase()} project
                      </p>
                    </div>
                    <div className="p-4 sm:p-6 grid sm:grid-cols-2 gap-3">
                      {currentCatalog.addons.map(addon => {
                        const isSelected = currentAddons.some(a => a.addon_id === addon.addon_id);
                        return (
                          <div
                            key={addon.addon_id}
                            className={`p-3 rounded-xl border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-[#0FA3B1] bg-[#0FA3B1]/5' 
                                : 'border-gray-100 hover:border-gray-200'
                            }`}
                            onClick={() => toggleAddon(addon)}
                            data-testid={`addon-${addon.addon_id}`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox checked={isSelected} className="mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-[#1F2933] text-sm">{addon.name}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Panel - COMBINED Project Summary */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 bg-[#1F2933]">
                  <h2 className="font-manrope font-semibold text-lg text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Your Project
                  </h2>
                  {Object.keys(projectTotals.categoryBreakdown).length > 1 && (
                    <p className="text-white/60 text-sm mt-1">
                      Multi-service estimate
                    </p>
                  )}
                </div>
                
                <div className="p-4 sm:p-6 max-h-[400px] overflow-y-auto">
                  {!hasProjectSelections ? (
                    <div className="text-center py-8 text-[#1F2933]/50">
                      <Calculator className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Add items to see your estimate</p>
                      <p className="text-xs mt-2">Browse categories on the left</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Show breakdown by category */}
                      {Object.entries(projectTotals.categoryBreakdown).map(([catName, breakdown]) => {
                        const catSlug = categories.find(c => c.name === catName)?.slug;
                        const roomData = projectCart.rooms[catSlug];
                        const itemData = projectCart.items[catSlug];
                        const bundleData = projectCart.bundles[catSlug];
                        const addonData = projectCart.addons[catSlug];
                        
                        return (
                          <div key={catName} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-sm font-semibold text-[#1F2933] flex items-center gap-2">
                                <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">{catName}</Badge>
                              </h3>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => clearCategory(catSlug)}
                                title={`Remove all ${catName} items`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                            
                            {/* Room selections */}
                            {roomData?.rooms.length > 0 && (
                              <div className="space-y-1 text-sm">
                                {roomData.rooms.map(roomKey => {
                                  const roomInfo = ROOM_PRICING[catSlug]?.[roomKey];
                                  return (
                                    <div key={roomKey} className="flex justify-between text-[#1F2933]/80">
                                      <span>{roomInfo?.label || roomKey}</span>
                                      <span className="text-[#1F2933]/60">
                                        {roomInfo && formatPrice(roomInfo.low, roomInfo.high)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Item selections */}
                            {itemData?.length > 0 && (
                              <div className="space-y-1 text-sm">
                                {itemData.map(item => (
                                  <div key={item.item_id} className="flex justify-between text-[#1F2933]/80">
                                    <span>{item.item_name} × {item.quantity}</span>
                                    <span className="text-[#1F2933]/60">
                                      {formatPrice(
                                        (item.labor_low + item.materials_low) * item.quantity,
                                        (item.labor_high + item.materials_high) * item.quantity
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Bundle selections */}
                            {bundleData?.length > 0 && (
                              <div className="space-y-1 text-sm">
                                {bundleData.map(bundle => (
                                  <div key={bundle.bundle_id} className="flex justify-between text-[#1F2933]/80">
                                    <span className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      {bundle.bundle_name}
                                    </span>
                                    <Badge className="bg-[#7ED957]/20 text-green-800 text-xs">Package</Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Add-ons for this category */}
                            {addonData?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-50">
                                <p className="text-xs text-[#1F2933]/50 mb-1">Add-ons applied:</p>
                                <div className="flex flex-wrap gap-1">
                                  {addonData.map(addon => (
                                    <Badge key={addon.addon_id} variant="outline" className="text-xs">
                                      {addon.addon_name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Category subtotal */}
                            <div className="mt-2 pt-2 flex justify-between text-sm font-medium">
                              <span className="text-[#1F2933]/70">Subtotal</span>
                              <span className="text-[#0FA3B1]">{formatPrice(breakdown.low, breakdown.high)}</span>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Grand Total */}
                      {Object.keys(projectTotals.categoryBreakdown).length > 0 && (
                        <div className="border-t-2 border-[#1F2933]/10 pt-4 mt-4">
                          <div className="flex justify-between text-lg font-bold">
                            <span className="text-[#1F2933]">Project Total</span>
                            <span className="text-[#0FA3B1]">
                              {formatPrice(projectTotals.totalLow, projectTotals.totalHigh)}
                            </span>
                          </div>
                          {Object.keys(projectTotals.categoryBreakdown).length > 1 && (
                            <p className="text-xs text-[#1F2933]/50 mt-1">
                              Combined estimate for {Object.keys(projectTotals.categoryBreakdown).length} services
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Continue Button */}
                <div className="p-4 sm:p-6 border-t border-gray-100">
                  <Button 
                    onClick={handleContinue}
                    className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl btn-shine"
                    disabled={!hasProjectSelections}
                    data-testid="continue-to-contractors-btn"
                  >
                    Continue to Pick Contractors <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <p className="text-xs text-center text-[#1F2933]/50 mt-3">
                    This is an estimate. Final pricing determined by contractors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
